// vg-binding: P7.D-24
//
// apps/vendor/src/features/products/add/CommonFields.tsx
//
// Phase 7 Wave 8 Task 28 — common-fields block shown in every Add
// Product method (catalog / file / api). The 5 required fields
// live here so the wizard's submit gate is method-agnostic.

import { useTranslation } from '../../../lib/i18n/index.js';

export interface CommonFieldsValue {
  name: string;
  sku_prefix: string;
  product_type: string;
  cost_price_cents: number;
  // Bug 7 — packaging dimensions moved to per-variant (VariantMatrix).
  // Amendment #4 — processing_time_days / capacity_per_day
  // weight_grams moved to per-variant (VariantMatrix / VariantOptionSelector).
  low_stock_threshold: number;
}

export const COMMON_FIELDS_DEFAULT: CommonFieldsValue = {
  name: '',
  sku_prefix: '',
  product_type: '',
  cost_price_cents: 0,
  low_stock_threshold: 20,
};

const INPUT_CLASS =
  'h-10 w-full rounded-md border border-pw-border bg-pw-bg px-3 text-sm text-pw-text focus:border-pw-primary focus:outline-none focus:ring-1 focus:ring-pw-primary';

const LABEL_CLASS = 'mb-1 block text-sm font-medium text-pw-text';

interface Props {
  value: CommonFieldsValue;
  onChange: (next: CommonFieldsValue) => void;
  /**
   * D16a — the product is priced per SKU: its cost is the minimum of the
   * variant prices, recomputed by the server, so the field is read-only.
   */
  costLocked?: boolean;
}

export function CommonFields({ value, onChange, costLocked = false }: Props): JSX.Element {
  const { t } = useTranslation('products');
  function patch(p: Partial<CommonFieldsValue>): void {
    onChange({ ...value, ...p });
  }
  return (
    <fieldset className="grid grid-cols-1 gap-3 md:grid-cols-2">
      <div>
        <label className={LABEL_CLASS}>{t('fields.name')}</label>
        <input
          data-testid="add-product-name"
          className={INPUT_CLASS}
          value={value.name}
          onChange={(e) => patch({ name: e.target.value })}
          maxLength={200}
          required
        />
      </div>
      <div>
        <label className={LABEL_CLASS}>{t('fields.skuPrefix')}</label>
        <input
          data-testid="add-product-sku-prefix"
          className={INPUT_CLASS}
          value={value.sku_prefix}
          onChange={(e) => patch({ sku_prefix: e.target.value.toUpperCase().slice(0, 10) })}
          maxLength={10}
          required
        />
      </div>
      <div>
        <label className={LABEL_CLASS}>{t('fields.productType')}</label>
        <input
          data-testid="add-product-type"
          className={INPUT_CLASS}
          value={value.product_type}
          onChange={(e) => patch({ product_type: e.target.value })}
          maxLength={80}
          required
        />
      </div>
      <div>
        <label className={LABEL_CLASS}>{t('fields.costPrice')}</label>
        <input
          type="number"
          min={0}
          step={0.01}
          data-testid="add-product-cost-price"
          className={`${INPUT_CLASS} disabled:cursor-not-allowed disabled:opacity-60`}
          value={(value.cost_price_cents / 100).toFixed(2)}
          onChange={(e) => {
            const v = Number.parseFloat(e.target.value);
            patch({ cost_price_cents: Number.isFinite(v) ? Math.max(0, Math.round(v * 100)) : 0 });
          }}
          disabled={costLocked}
          required
        />
        {costLocked ? (
          <p className="mt-1 text-xs text-pw-text-muted" data-testid="add-product-cost-price-locked">
            {t('fields.costPricePerSku')}
          </p>
        ) : null}
      </div>
      <div>
        <label className={LABEL_CLASS}>{t('fields.lowStockThreshold')}</label>
        <input
          type="number"
          min={0}
          step={1}
          className={INPUT_CLASS}
          value={value.low_stock_threshold}
          onChange={(e) => patch({ low_stock_threshold: Math.max(0, Number.parseInt(e.target.value, 10) || 0) })}
        />
      </div>
      {/* Bug 7 — dimensions moved to per-variant; edited in the VariantMatrix
          cells below, not here at product level. */}
    </fieldset>
  );
}

export default CommonFields;
